import {Store} from "vuex";
import {DataPropertyProfileMap, TradeContractBase} from "@/store/modules/hcs/tradeContract/model";
import {Account} from "@/store/modules/hcs";
import deepEquals from "fast-deep-equal";
import {TRADE_CONTRACT_SERVICE_TYPES} from "@/constants";
import {AxiosInstance} from "axios";
import {toDateTime} from "@/utils/Formatter";
import {RootStateType} from "@/store";
import {ContractProfile} from "@/store/modules/hcs/contractProfile/model";

type Nullable<T> = {
  [P in keyof T]?: T[P] | null;
};

/**
 * Store 未初期化時に発生する例外
 */
export class UninitializedStoreError extends Error {
  constructor() {
    super("The store is not ready.");
  }
}

/**
 * 未実装メソッドを呼び出した際に発生する例外
 */
export class NotImplementedError extends Error {
    constructor() {
        super("This method is not implemented.");
    }
}

export const loadAllContractProfiles = async <T extends TradeContractBase> (tradeContracts: TradeContractBaseView<T>[]) => {
  for(const tradeContract of tradeContracts) {
    await tradeContract.loadContractProfiles();
  }
};

export class ErrorArray<T extends string> extends Array<T> {
  containsCategory(category: string): boolean {
      return this.some(e => e.startsWith(`${category}.`));
  }
}

/**
 * TradeContract 生成の為のデータソース
 */
export default abstract class TradeContractBaseView<TradeContractSource extends TradeContractBase = TradeContractBase> {
  /** 変更フラグ */
  dirty = false;

  /** ストア */
  readonly $store: Store<RootStateType>

  /** 取引契約 */
  readonly tradeContract: TradeContractSource;

  /** 契約内容 */
  private readonly _contractProfiles: ContractProfile[];

  /**
   * コンストラクタ
   *
   * @param tradeContract TradeContractSource
   * @param $store Store<RootStateType>
   */
  protected constructor(tradeContract: TradeContractSource, $store: Store<RootStateType>) {
    this.$store = $store;
    this.tradeContract = tradeContract;
    this._contractProfiles = [];
  }

  /**
   * すべての ContractProfile をロードします
   */
  abstract loadContractProfiles(): Promise<void>;

  /**
   * すべての ContractProfile を置き換えます
   *
   * @param contractProfiles すべての ContractProfile
   */
  set contractProfiles(contractProfiles: ContractProfile[]) {
    this._contractProfiles.splice(0, this._contractProfiles.length, ...contractProfiles);
  }

  /**
   * すべての ContractProfile を取得します
   */
  get contractProfiles() {
    return this._contractProfiles;
  }

  /** アカウント情報 */
  get account(): Partial<Account> {
    return this.$store?.getters?.['hcs/account'] ?? {};
  }

  /** hcs/hcsClient */
  get hcsClient(): AxiosInstance {
    const mayAxios = this.$store?.getters?.['hcs/client'];
    if(mayAxios) {
      return mayAxios;
    }
    throw new UninitializedStoreError();
  }

  /** 自分が発行した取引契約かどうか */
  get amiIssuer() {
    if(this.isSubscribeMode) {
      return this.recipientTerminalId === this.selfTerminalId;
    } else {
      return this.issuerTerminalId === this.selfTerminalId;
    }
  }

  /** 相手が発行した取引契約かどうか */
  get amiRecipient() {
    if(this.isSubscribeMode) {
      return this.issuerTerminalId === this.selfTerminalId;
    } else {
      return this.recipientTerminalId === this.selfTerminalId;
    }
  }

  /** 自分が起案側かどうか */
  get amiProposer() {
    return this.proposerTerminalId === this.selfTerminalId;
  }

  /** 自分が適応側かどうか */
  get amiResponder() {
    return this.responderTerminalId === this.selfTerminalId;
  }

  /**
   * 申請型モードかどうかを取得します
   */
  get isRequestMode() {
    return this.isRequestDraftMode || this.isRequestOfferMode;
  }

  /**
   * 募集型モードかどうかを取得します
   */
  get isPublishMode() {
    return false;
  }

  /**
   * 申込型モードかどうかを取得します
   */
  get isSubscribeMode() {
    return false;
  }

  /**
   * 起案申請型モードかどうかを取得します
   */
  get isRequestDraftMode() {
    return this.contractType === 'draft';
  }

  /**
   * 申込申請型モードかどうかを取得します
   */
  get isRequestOfferMode() {
    return this.contractType === 'offer';
  }

  /**
   * 自分のターミナルID
   */
  get selfTerminalId() {
    if(this.isSelfConsume) {
      return this.tradeContractSource.consumerTerminalId;
    } else if(this.isSelfProduce) {
      return this.tradeContractSource.producerTerminalId;
    } else {
      return null;
    }
  }

  /**
   * 相手ターミナルID
   */
  get otherTerminalId() {
    if(this.isSelfConsume) {
      return this.tradeContractSource.producerTerminalId;
    } else if(this.isSelfProduce) {
      return this.tradeContractSource.consumerTerminalId;
    } else {
      return null;
    }
  }

  /** ID */
  get id() {
    return this.tradeContractSource.id;
  }

  /** 契約の名称 */
  get name() {
    return this.tradeContractSource.name;
  }
  set name(name) {
    this.updateTradeContractSource({name});
  }

  /** 契約内容の簡単な説明 */
  get description() {
    return this.tradeContractSource.description;
  }
  set description(description) {
    this.updateTradeContractSource({description});
  }

  /**
   * 起案ターミナルID
   */
  get proposerTerminalId() {
    return this.tradeContractSource.proposerTerminalId;
  }

  /**
   * 適応ターミナルID
   */
  get responderTerminalId() {
    return this.tradeContractSource.responderTerminalId;
  }

  /**
   * 発行ターミナルID
   */
  get issuerTerminalId() {
    return this.tradeContractSource.issuerTerminalId;
  }

  /**
   * 受領ターミナルID
   */
  get recipientTerminalId() {
    return this.tradeContractSource.recipientTerminalId;
  }

  /** 自分データプロファイルID */
  get selfDataProfileId() {
    if(this.isSelfConsume) {
      return this.tradeContractSource.consumerDataProfileId;
    } else if(this.isSelfProduce) {
      return this.tradeContractSource.producerDataProfileId;
    } else {
      return null;
    }
  }
  set selfDataProfileId(selfDataProfileId) {
    if(this.isSelfConsume) {
      this.updateTradeContractSource({consumerDataProfileId: selfDataProfileId});
    } else if(this.isSelfProduce) {
      this.updateTradeContractSource({producerDataProfileId: selfDataProfileId});
    }
  }

  /** 相手データプロファイルID */
  get otherDataProfileId() {
    if(this.isSelfConsume) {
      return this.tradeContractSource.producerDataProfileId;
    } else if(this.isSelfProduce) {
      return this.tradeContractSource.consumerDataProfileId;
    } else {
      return null;
    }
  }

  /** 利用側データプロファイルID */
  get consumerDataProfileId() {
    return this.tradeContractSource.consumerDataProfileId;
  }

  /** 提供側データプロファイルID */
  get producerDataProfileId() {
    return this.tradeContractSource.producerDataProfileId;
  }

  /** 起案データプロファイルID */
  get proposerDataProfileId() {
    if(this.tradeContractSource.proposerTerminalId === this.tradeContractSource.producerTerminalId) {
      return this.tradeContractSource.producerDataProfileId;
    } else {
      return this.tradeContractSource.consumerDataProfileId;
    }
  }

  /** 適応データプロファイルID */
  get responderDataProfileId() {
    if(this.tradeContractSource.responderTerminalId === this.tradeContractSource.producerTerminalId) {
      return this.tradeContractSource.producerDataProfileId;
    } else {
      return this.tradeContractSource.consumerDataProfileId;
    }
  }

  /** 自分サービスプロファイルID */
  get selfServiceProfileId() {
    if(this.isSelfConsume) {
      return this.tradeContractSource.consumerServiceProfileId;
    } else if(this.isSelfProduce) {
      return this.tradeContractSource.producerServiceProfileId;
    } else {
      return null;
    }
  }
  set selfServiceProfileId(selfServiceProfileId) {
    if(this.isSelfConsume) {
      this.updateTradeContractSource({consumerServiceProfileId: selfServiceProfileId});
    } else if(this.isSelfProduce) {
      this.updateTradeContractSource({producerServiceProfileId: selfServiceProfileId});
    }
  }

  /** 相手サービスプロファイルID */
  get otherServiceProfileId() {
    if(this.isSelfConsume) {
      return this.tradeContractSource.producerServiceProfileId;
    } else if(this.isSelfProduce) {
      return this.tradeContractSource.consumerServiceProfileId;
    } else {
      return null;
    }
  }

  /** 起案サービスプロファイルID */
  get proposerServiceProfileId() {
    if(this.tradeContractSource.proposerTerminalId === this.tradeContractSource.producerTerminalId) {
      return this.tradeContractSource.producerServiceProfileId;
    } else {
      return this.tradeContractSource.consumerServiceProfileId;
    }
  }

  /** 適応サービスプロファイルID */
  get responderServiceProfileId() {
    if(this.tradeContractSource.responderTerminalId === this.tradeContractSource.producerTerminalId) {
      return this.tradeContractSource.producerServiceProfileId;
    } else {
      return this.tradeContractSource.consumerServiceProfileId;
    }
  }

  /** 自分プロセスプロファイルID */
  get selfProcessProfileId() {
    if(this.isSelfConsume) {
      return this.tradeContractSource.consumerProcessProfileId;
    } else if(this.isSelfProduce) {
      return this.tradeContractSource.producerProcessProfileId;
    } else {
      return null;
    }
  }
  set selfProcessProfileId(selfProcessProfileId) {
    if(this.isSelfConsume) {
      this.updateTradeContractSource({consumerProcessProfileId: selfProcessProfileId});
    } else if(this.isSelfProduce) {
      this.updateTradeContractSource({producerProcessProfileId: selfProcessProfileId});
    }
  }

  /** 相手プロセスプロファイルID */
  get otherProcessProfileId() {
    if(this.isSelfConsume) {
      return this.tradeContractSource.producerProcessProfileId;
    } else if(this.isSelfProduce) {
      return this.tradeContractSource.consumerProcessProfileId;
    } else {
      return null;
    }
  }

  /** 起案プロセスプロファイルID */
  get proposerProcessProfileId() {
    if(this.tradeContractSource.proposerTerminalId === this.tradeContractSource.producerTerminalId) {
      return this.tradeContractSource.producerProcessProfileId;
    } else {
      return this.tradeContractSource.consumerProcessProfileId;
    }
  }

  /** 適応プロセスプロファイルID */
  get responderProcessProfileId() {
    if(this.tradeContractSource.responderTerminalId === this.tradeContractSource.producerTerminalId) {
      return this.tradeContractSource.producerProcessProfileId;
    } else {
      return this.tradeContractSource.consumerProcessProfileId;
    }
  }

  /** 自分イベントプロファイルIDの配列 */
  get selfEventProfileIds() {
    if(this.isSelfConsume) {
      return this.tradeContractSource.consumerEventProfileIds || [];
    } else if(this.isSelfProduce) {
      return this.tradeContractSource.producerEventProfileIds || [];
    } else {
      return [];
    }
  }
  set selfEventProfileIds(selfEventProfileIds) {
    if(this.isSelfConsume) {
      this.updateTradeContractSource({consumerEventProfileIds: selfEventProfileIds});
    } else if(this.isSelfProduce) {
      this.updateTradeContractSource({producerEventProfileIds: selfEventProfileIds});
    }
  }

  /** 相手イベントプロファイルIDの配列 */
  get otherEventProfileIds() {
    if(this.isSelfConsume) {
      return this.tradeContractSource.producerEventProfileIds || [];
    } else if(this.isSelfProduce) {
      return this.tradeContractSource.consumerEventProfileIds || [];
    } else {
      return [];
    }
  }

  /** 起案イベントプロファイルIDの配列 */
  get proposerEventProfileIds() {
    if (this.tradeContractSource.proposerTerminalId === this.tradeContractSource.producerTerminalId) {
      return this.tradeContractSource.producerEventProfileIds || [];
    } else {
      return this.tradeContractSource.consumerEventProfileIds || [];
    }
  }

  /** 適応イベントプロファイルIDの配列 */
  get responderEventProfileIds() {
    if (this.tradeContractSource.responderTerminalId === this.tradeContractSource.producerTerminalId) {
      return this.tradeContractSource.producerEventProfileIds || [];
    } else {
      return this.tradeContractSource.consumerEventProfileIds || [];
    }
  }

  /** データ項目プロパティーマップ */
  get dataPropertyProfileMaps(): DataPropertyProfileMap[] {
    return this.tradeContractSource.dataPropertyProfileMaps;
  }
  set dataPropertyProfileMaps(dataPropertyProfileMaps) {
    this.updateTradeContractSource({dataPropertyProfileMaps}, false);
  }

  /** 選択された契約プロファイルテンプレートID */
  get contractProfileTemplateId() {
    return this.tradeContractSource.contractProfileTemplateId;
  }
  set contractProfileTemplateId(contractProfileTemplateId) {
    this.updateTradeContractSource({contractProfileTemplateId});
  }

  /** 契約作成中に相手に承諾か拒否のいずれかの選択を求めることを示すフラグ */
  get unchangeable() {
    return this.tradeContractSource.unchangeable;
  }
  set unchangeable(unchangeable) {
    this.updateTradeContractSource({unchangeable});
  }

  /** 自分側の利用 (consume) or 提供 (produce) */
  get serviceType() {
    switch(this.tradeContractSource.serviceType as string) {
      case 'consume':
      case 'consumer':
        return 'consume';
      case 'produce':
      case 'producer':
        return 'produce';
      case 'common':
        return 'common';
      default:
        return undefined;
    }
  }

  /** 自分側の「提供」「利用」文字列 */
  get serviceTypeText() {
    const serviceType = this.serviceType;
    switch(serviceType) {
      case 'produce':
        return TRADE_CONTRACT_SERVICE_TYPES.producer;
      case 'consume':
        return TRADE_CONTRACT_SERVICE_TYPES.consumer;
      default:
        return '';
    }
  }

  /** 相手側の利用 (consume) or 提供 (produce) */
  get otherServiceType() {
    switch(this.tradeContractSource.serviceType as string) {
      case 'consume':
      case 'consumer':
        return 'produce';
      case 'produce':
      case 'producer':
        return 'consume';
      case 'common':
        return 'common';
      default:
        return undefined;
    }
  }

  /** 自分側のサービスタイプが利用の場合 true そうでない場合 false */
  get isSelfConsume() {
    return ['common', 'consume'].includes(this.serviceType ?? '');
  }

  /** 相手側のサービスタイプが利用の場合 true そうでない場合 false */
  get isOtherConsume() {
    return ['common', 'consume'].includes(this.otherServiceType ?? '');
  }

  /** 自分側のサービスタイプが提供の場合 true そうでない場合 false */
  get isSelfProduce() {
    return ['common', 'produce'].includes(this.serviceType ?? '');
  }

  /** 相手側のサービスタイプが提供の場合 true そうでない場合 false */
  get isOtherProduce() {
    return ['common', 'produce'].includes(this.otherServiceType ?? '');
  }

  /** 共通辞書ID */
  get commonDictionaryId() {
    return this.tradeContractSource.commonDictionaryId;
  }

  /** sendType */
  get sendType() {
    return this.tradeContractSource.sendType;
  }
  set sendType(sendType) {
    this.updateTradeContractSource({sendType});
  }

  /** contractType */
  get contractType() {
    return this.tradeContractSource.contractType;
  }

  /** 作成日時 */
  get createdAt() {
    const createdAt = toDateTime(this.tradeContractSource.createdAt);
    if(createdAt?.isValid) {
      return createdAt;
    }
    return null;
  }

  /** 更新日時 */
  get updatedAt() {
    const updatedAt = toDateTime(this.tradeContractSource.updatedAt);
    if(updatedAt?.isValid) {
      return updatedAt;
    }
    return null;
  }

  /** tradeContract を取得します */
  get tradeContractSource(): TradeContractSource {
    return this.tradeContract;
  }

  /** 変更フラグを立てます */
  markDirty() {
    this.dirty = true;
  }

  updateTradeContractSource<TradeContractSourceUpdate = Nullable<TradeContractSource>>(tradeContract: TradeContractSourceUpdate, markDirtyIfChanged = true) {
    Object.entries(tradeContract).forEach(([key, value]) => {
      const k = key as keyof TradeContractSource;
      if(markDirtyIfChanged && !deepEquals(this.tradeContract[k], value)) {
        this.markDirty();
      }
      this.tradeContract[k] = value;
    });
  }

  /**
   * ContractProfile を取得します
   */
  fetchContractProfiles(): Promise<ContractProfile[]> {
    throw new NotImplementedError();
  }
}
